import React from "react";
import heroArt from "../assets/stealth-hero.jpg";
import shipArt from "../assets/stealth-ship.webp";
import { StealthRobot, useArrivalState } from "./robot/StealthRobot";

// Ship centre in the artwork (percent of width / height) — rings and glow are anchored here.
const CX = 50, CY = 42;
// Spacecraft cut-out bounds in the artwork (percent), so it sits exactly over the ship in the background.
const SHIP = { left: 2.734, top: 29.395, width: 94.727, height: 30.957 };

const GLOW = {
  idle:      { c: "220,220,220", o: 0.10, dur: "6s" },
  listening: { c: "255,255,255", o: 0.30, dur: "1.4s" },
  thinking:  { c: "230,230,230", o: 0.22, dur: "2.4s" },
  speaking:  { c: "255,255,255", o: 0.34, dur: "1.1s" },
};

function Rings() {
  return (
    <svg viewBox="0 0 100 100" className="hud-rings" aria-hidden="true">
      <g stroke="rgba(255,255,255,0.55)" strokeWidth="0.25">
        <line x1="50" y1="2" x2="50" y2="12" /><line x1="50" y1="88" x2="50" y2="98" />
        <line x1="2" y1="50" x2="9" y2="50" strokeDasharray="1.4 1" /><line x1="91" y1="50" x2="98" y2="50" strokeDasharray="1.4 1" />
      </g>
      <g className="hud-ring hud-cw hud-slow">
        <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="0.55" strokeLinecap="round" strokeDasharray="14 9 4 9 22 12" />
        <circle cx="50" cy="50" r="46.5" fill="none" stroke="rgba(233,233,233,0.45)" strokeWidth="0.25" strokeDasharray="0.4 1.6" />
      </g>
      <g className="hud-ring hud-ccw hud-mid">
        <circle cx="50" cy="50" r="37" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.35" strokeDasharray="30 6 2 6" />
        <circle cx="50" cy="50" r="34" fill="none" stroke="rgba(232,232,232,0.35)" strokeWidth="0.2" strokeDasharray="5 3" />
      </g>
      <g className="hud-ring hud-ccw hud-fast">
        <circle cx="50" cy="50" r="27" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="0.45" strokeLinecap="round" strokeDasharray="8 5 1.5 5" />
      </g>
      <circle cx="50" cy="50" r="44" fill="none" className="hud-shimmer" stroke="rgba(228,228,228,0.6)" strokeWidth="0.7" strokeDasharray="6 270" strokeLinecap="round" />
    </svg>
  );
}

function HeroRobot() {
  const phase = useArrivalState(true, true); // waves hello when the welcome scene opens, then idles
  return <StealthRobot size="10%" state={phase} className="hero-robot" testid="hero-robot" />;
}

function Art({ state, title }) {
  const g = GLOW[state] || GLOW.idle;
  return (
    <>
      <img src={heroArt} alt="Stealth spaceship over Earth" className="spaceship-hero-img" draggable="false" decoding="async" data-testid="spaceship-hero-image" />
      <div className="spaceship-glow" style={{ left: `${CX}%`, top: `${CY}%`, "--glow": `rgba(${g.c},${g.o})`, animationDuration: g.dur }} data-testid="spaceship-glow" />
      <div className="hud-anchor" style={{ left: `${CX}%`, top: `${CY}%`, width: "76%", aspectRatio: "1 / 1" }} data-testid="hud-rings">
        <Rings />
      </div>
      <img src={shipArt} alt="" aria-hidden="true" className="ship-layer" draggable="false" decoding="async" data-testid="spaceship-foreground"
        style={{ left: `${SHIP.left}%`, top: `${SHIP.top}%`, width: `${SHIP.width}%`, height: `${SHIP.height}%` }} />
      {title && <HeroRobot />}
      {title && (
        <div className="spaceship-title" style={{ top: `${CY - 26}%` }} data-testid="hero-title">
          <div className="font-heading font-light text-2xl sm:text-3xl tracking-[0.55em] text-white/95 pl-[0.55em]" style={{ textShadow: "0 0 18px rgba(233,233,233,.35)" }}>STEALTH</div>
          <div className="text-[10px] sm:text-xs tracking-[0.85em] text-white/75 mt-2 pl-[0.85em]">ASSISTANCE</div>
        </div>
      )}
    </>
  );
}

// full: edge-to-edge scene across the whole chat page (homepage); otherwise a compact square (during chats).
export function SpaceshipHero({ state = "idle", full = false, size = 200 }) {
  if (!full) {
    return (
      <div className="spaceship-hero is-compact" style={{ width: `min(${size}px, 60vw, 30vh)`, aspectRatio: "1 / 1" }} data-testid="ai-orb-container" data-state={state}>
        <Art state={state} />
      </div>
    );
  }
  return (
    <div className="spaceship-hero is-full" data-testid="ai-orb-container" data-state={state}>
      <div className="hero-horizon" aria-hidden="true" />
      <div className="hero-art"><Art state={state} title /></div>
    </div>
  );
}
